import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, ArrowLeft, Package, DollarSign, Box, Info, Truck, Settings } from "lucide-react";
import { adminApi, categoriesApi, collectionsApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";

export default function AddProductPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [collections, setCollections] = useState<any[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    price: "",
    original_price: "",
    buyoutprice: "",
    stock: "",
    collection_id: "",
    category_id: "",
    is_active: true,
    dimensions: "",
    care_tips: "",
    shipping_info: "",
    style_tip: "",
    tax_included: false,
    cod_available: false,
    returns_available: true,
    express_delivery_available: false,
    free_shipping_threshold: "500",
    quality_description: "",
  });

  useEffect(() => {
    fetchCategoriesAndCollections();
  }, []);

  const fetchCategoriesAndCollections = async () => {
    try {
      const [categoriesData, collectionsData] = await Promise.all([
        categoriesApi.getAll(),
        collectionsApi.getAll(),
      ]);
      setCategories(categoriesData.categories || categoriesData || []);
      setCollections(collectionsData.collections || collectionsData || []);
    } catch (error) {
      console.error("Error fetching categories/collections:", error);
      toast.error("Failed to load categories and collections");
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({ ...formData, name });
    if (!formData.slug || formData.slug === generateSlug(formData.name)) {
      setFormData((prev) => ({ ...prev, slug: generateSlug(name) }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : type === "number" ? value : value,
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    if (!formData.category_id || !formData.collection_id) {
      toast.error("Please select both Category and Collection");
      return;
    }

    setLoading(true);

    try {
      // Create product first
      const productData = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description || null,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        buyoutprice: formData.buyoutprice ? parseFloat(formData.buyoutprice) : null,
        stock: parseInt(formData.stock) || 0,
        collection_id: formData.collection_id ? parseInt(formData.collection_id) : null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        is_active: formData.is_active,
        dimensions: formData.dimensions || null,
        care_tips: formData.care_tips || null,
        shipping_info: formData.shipping_info || null,
        style_tip: formData.style_tip || null,
        tax_included: formData.tax_included,
        cod_available: formData.cod_available,
        returns_available: formData.returns_available,
        express_delivery_available: formData.express_delivery_available,
        free_shipping_threshold: formData.free_shipping_threshold
          ? parseFloat(formData.free_shipping_threshold)
          : null,
        quality_description: formData.quality_description || null,
      };

      const response = await adminApi.createProduct(productData, token);
      const productId = response.data?.id || response.id;

      if (!productId) {
        throw new Error("Failed to create product");
      }

      toast.success("Product created successfully!");
      navigate(`/admin/products/${productId}`);
    } catch (error: any) {
      console.error("Error creating product:", error);
      toast.error(error.response?.data?.detail || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/products"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aarsa-sage-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">Add New Product</h1>
        <p className="text-xs text-gray-500">Create a new product in your catalog</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Category & Collection - First Step */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Package className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Select Category & Collection</h2>
                <span className="text-xs text-red-500 ml-auto">* Required</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-3">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {categories.length === 0 ? (
                      <p className="text-xs text-gray-500">No categories available</p>
                    ) : (
                      categories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, category_id: String(cat.id) })}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                            formData.category_id === String(cat.id)
                              ? "bg-gradient-to-r from-aarsa-sage-600 to-aarsa-gold-600 text-white shadow-sm"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-3">
                    Collection <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {collections.length === 0 ? (
                      <p className="text-xs text-gray-500">No collections available</p>
                    ) : (
                      collections.map((col) => (
                        <button
                          key={col.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, collection_id: String(col.id) })}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                            formData.collection_id === String(col.id)
                              ? "bg-gradient-to-r from-aarsa-sage-600 to-aarsa-gold-600 text-white shadow-sm"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                          }`}
                        >
                          {col.title}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Row 1: Basic Information + Pricing */}
          <div className="grid grid-cols-2 gap-3">
            {/* Basic Information */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <Info className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Basic Information</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleNameChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Enter product name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Slug <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="product-slug"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                    placeholder="Enter product description"
                  />
                </div>
              </div>
            </motion.div>

            {/* Pricing & Inventory */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Pricing & Inventory</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Original Price (₹)
                  </label>
                  <input
                    type="number"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Buyout Price (₹)
                  </label>
                  <input
                    type="number"
                    name="buyoutprice"
                    value={formData.buyoutprice}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Row 2: Product Details + Shipping & Options */}
          <div className="grid grid-cols-2 gap-3">
            {/* Product Details */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <Box className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Product Details</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Dimensions</label>
                  <input
                    type="text"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="e.g., 23*8.5 cm, 115 g"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Quality Description</label>
                  <input
                    type="text"
                    name="quality_description"
                    value={formData.quality_description}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Enter quality information"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Care Tips</label>
                  <textarea
                    name="care_tips"
                    value={formData.care_tips}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                    placeholder="Enter care instructions"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Style Tip</label>
                  <textarea
                    name="style_tip"
                    value={formData.style_tip}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                    placeholder="Enter styling suggestions"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Shipping Info</label>
                  <textarea
                    name="shipping_info"
                    value={formData.shipping_info}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                    placeholder="Enter shipping information"
                  />
                </div>
              </div>
            </motion.div>

            {/* Shipping & Options */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <Truck className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Shipping & Options</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Free Shipping Threshold (₹)
                  </label>
                  <input
                    type="number"
                    name="free_shipping_threshold"
                    value={formData.free_shipping_threshold}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="500.00"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="tax_included"
                      checked={formData.tax_included}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                    />
                    <span className="text-xs font-medium text-gray-700">Tax Included</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="cod_available"
                      checked={formData.cod_available}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                    />
                    <span className="text-xs font-medium text-gray-700">COD Available</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="returns_available"
                      checked={formData.returns_available}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                    />
                    <span className="text-xs font-medium text-gray-700">Returns Available</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="express_delivery_available"
                      checked={formData.express_delivery_available}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                    />
                    <span className="text-xs font-medium text-gray-700">Express Delivery</span>
                  </label>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Status */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Status</h2>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                  />
                  <span className="text-xs font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-2 pb-2">
            <Link
              to="/admin/products"
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Create Product
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
